/**
 * Backward compatibility for legacy PRE_POST URLs.
 *
 * State, the URL and the saved-view definition now share one vocabulary:
 *
 *   window | state / URL key
 *   -------|----------------------------------------------------------
 *   Main   | periodType / periodStartDate / periodEndDate
 *          | (the POST window under PRE_POST, the single active window
 *          |  in every other mode)
 *   Pre    | prePeriodType / prePeriodStartDate / prePeriodEndDate
 *          | (PRE_POST only)
 *
 * Because the names match, serialisation needs no mapping at all — a state
 * field is written under its own name, and a parsed param lands on the field
 * of the same name.
 *
 * What still needs translating is the LEGACY spelling, which named the two
 * windows the other way round: `period*` was the PRE window and `post*` the
 * POST window. Bookmarked links, shared links and backend-generated view URLs
 * still use it, so the reader accepts it and rewrites it onto the current
 * field names.
 *
 * Remove this module once the backend emits the current convention and old
 * links have aged out — that is the follow-up the ticket scheduled.
 */

import {
  MODE,
  PERIOD_END_DATE,
  PERIOD_START_DATE,
  PERIOD_TYPE,
  POST_END_DATE,
  POST_PERIOD_TYPE,
  POST_START_DATE,
  PRE_POST,
  PRE_PERIOD_END_DATE,
  PRE_PERIOD_START_DATE,
  PRE_PERIOD_TYPE,
} from "@/constant/constantsThrougoutProject";

/** Legacy URL key → current state field. */
const LEGACY_URL_TO_STATE_KEY: Readonly<Record<string, string>> = {
  // Legacy `period*` was the PRE window.
  [PERIOD_TYPE]: PRE_PERIOD_TYPE,
  [PERIOD_START_DATE]: PRE_PERIOD_START_DATE,
  [PERIOD_END_DATE]: PRE_PERIOD_END_DATE,
  // Legacy `post*` was the POST window, which is now the main one.
  [POST_PERIOD_TYPE]: PERIOD_TYPE,
  [POST_START_DATE]: PERIOD_START_DATE,
  [POST_END_DATE]: PERIOD_END_DATE,
};

/** Keys only the current convention can produce. */
const CURRENT_CONVENTION_MARKERS = [
  PRE_PERIOD_TYPE,
  PRE_PERIOD_START_DATE,
  PRE_PERIOD_END_DATE,
];

/**
 * Keys only the legacy convention can produce. A legacy PRE_POST URL
 * effectively always carries `postPeriodType` (it had a non-empty default),
 * so this fires even when both windows are symbolic and carry no dates.
 */
const LEGACY_CONVENTION_MARKERS = [
  POST_PERIOD_TYPE,
  POST_START_DATE,
  POST_END_DATE,
];

function hasValue(parsed: Record<string, unknown>, key: string): boolean {
  const value = parsed[key];
  return value != null && value !== "";
}

/**
 * Whether a parsed PRE_POST URL uses the current convention rather than the
 * legacy one.
 *
 * A current-convention marker wins over a legacy one, so a URL carrying both
 * is read as current. With no marker at all the current convention is
 * assumed: it is what every newly generated URL uses.
 */
export function usesCurrentPrePostUrlKeys(
  parsed: Record<string, unknown>,
): boolean {
  if (CURRENT_CONVENTION_MARKERS.some((key) => hasValue(parsed, key))) return true;
  if (LEGACY_CONVENTION_MARKERS.some((key) => hasValue(parsed, key))) return false;
  return true;
}

/**
 * Rewrites a legacy PRE_POST URL record onto the current state field names.
 *
 * A URL already in the current convention is keyed by state field names and
 * passes through untouched, as does every non-PRE_POST record.
 *
 * Returns a new object; the input is never mutated.
 */
export function remapPrePostPeriodKeysFromURL<T extends Record<string, unknown>>(
  parsed: T,
): T {
  if (parsed[MODE] !== PRE_POST) return parsed;
  if (usesCurrentPrePostUrlKeys(parsed)) return parsed;

  const remapped: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(parsed)) {
    remapped[LEGACY_URL_TO_STATE_KEY[key] ?? key] = value;
  }
  return remapped as T;
}
